import { PROTOCOL_VERSION, type ClientMsg, type ServerMsg } from '../../../shared/protocol';

const RESUME_KEY = 'tankshift.resume';

export type NetStatus = 'connecting' | 'open' | 'closed' | 'version';

function readResume(): string | undefined {
  try {
    return sessionStorage.getItem(RESUME_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeResume(v: string): void {
  try {
    sessionStorage.setItem(RESUME_KEY, v);
  } catch {
    /* indisponible */
  }
}

/** Connexion WebSocket avec reprise de session : une reconnexion dans les 30 s retrouve le même tank. */
export class NetClient {
  private ws: WebSocket | null = null;
  private stopped = false;
  private retry = 0;
  status: NetStatus = 'connecting';
  bytesIn = 0;

  constructor(
    private readonly hello: () => Omit<Extract<ClientMsg, { t: 'hello' }>, 't' | 'v' | 'resume'>,
    private readonly onMessage: (m: ServerMsg) => void,
    private readonly onStatus: (s: NetStatus) => void,
  ) {}

  connect(): void {
    if (this.stopped) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    this.setStatus('connecting');
    ws.onopen = () => {
      this.retry = 0;
      ws.send(JSON.stringify({ t: 'hello', v: PROTOCOL_VERSION, ...this.hello(), resume: readResume() }));
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return;
      this.bytesIn += ev.data.length;
      let m: ServerMsg;
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (m.t === 'welcome') {
        writeResume(m.resume);
        this.setStatus('open');
      } else if (m.t === 'err' && m.code === 'version') {
        this.stopped = true;
        this.setStatus('version');
      }
      this.onMessage(m);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return;
      this.setStatus('closed');
      const delay = Math.min(5000, 400 * 2 ** this.retry++);
      setTimeout(() => this.connect(), delay);
    };
  }

  send(m: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Sortie volontaire (bouton Retour, fin de match, nouveau lancement) : le tank est abandonné, et le prochain
   *  « Jouer » démarre dans le mode choisi au lieu de reprendre l'ancienne arène. Un rechargement de la page ne
   *  passe pas par ici : la reprise reste possible pendant 30 s. */
  close(): void {
    this.stopped = true;
    try {
      sessionStorage.removeItem(RESUME_KEY);
    } catch {
      /* indisponible */
    }
    this.ws?.close();
    this.ws = null;
  }

  private setStatus(s: NetStatus): void {
    this.status = s;
    this.onStatus(s);
  }
}
